/**
 * Copying, with the colours.
 *
 * The plain text is the easy half. The interesting half is that a clipboard
 * carries several representations of the same thing at once, and an
 * application takes the richest one it understands: paste into Word, Google
 * Docs, Pages, Outlook or Notion and they read `text/html`, so the tone
 * colours survive; paste into a code editor or a terminal and they read
 * `text/plain`, so nothing arrives full of markup.
 *
 * Writing both is what makes one button right in both places.
 */

/** The two representations put on the clipboard together. */
export interface CopyFlavours {
  readonly text: string;
  readonly html: string;
}

/** How a copy attempt ended, which is what the button reports. */
export type CopyResult = "copied" | "copied-without-colours" | "failed";

const escapes = new Map<string, string>([
  ["&", "&amp;"],
  ["<", "&lt;"],
  [">", "&gt;"],
  ['"', "&quot;"],
]);

/**
 * The pattern that finds them, built from the map above.
 *
 * Written out as a literal, the character class and the map were two lists that
 * had to agree, and the `??` that stood in the replacement was there for the
 * day they stopped agreeing. Deriving one from the other is what makes the
 * lookup answer.
 */
const escapable = new RegExp(`[${[...escapes.keys()].join("")}]`, "gu");

/**
 * Escape text for markup.
 *
 * Applied to the converted text as well as to anything else, because a
 * conversion passes through everything that is not Han exactly as it was
 * written, so a `<` somebody typed reaches here as a `<`.
 */
export function escapeHtml(text: string): string {
  return text.replaceAll(escapable, (character) => escapes.get(character)!);
}

/**
 * Escape text for markup, keeping its line breaks.
 *
 * A newline is whitespace in HTML, so a paragraph pasted from a two-line input
 * would arrive as one line. Word processors honour `<br>`, which is the one
 * piece of structure worth carrying across.
 */
export function escapeHtmlLines(text: string): string {
  return escapeHtml(text).replaceAll("\n", "<br>");
}

/**
 * Wrap a coloured fragment in something a word processor will accept.
 *
 * `white-space: pre-wrap` keeps runs of spaces, which matters because word
 * spacing is the visible half of standard pinyin orthography and a collapsed
 * double space would silently change what was copied.
 */
export function clipboardDocument(fragment: string): string {
  return `<span style="white-space:pre-wrap">${fragment}</span>`;
}

/**
 * Put both representations on the clipboard.
 *
 * Falls back to plain text on a browser without `ClipboardItem` — Firefox only
 * enabled it in 127 — and reports which of the two happened, so the button can
 * say "Copied" or "Copied (without colours)" truthfully rather than claiming
 * the colours went along.
 *
 * The clipboard API needs a user gesture and it needs the document focused, so
 * this has to be called straight out of a click handler. Anything it throws is
 * a refusal rather than a bug: a denied permission and a browser that has
 * never heard of the API both land on `failed`.
 */
export async function copyFlavours(
  flavours: CopyFlavours,
): Promise<CopyResult> {
  const clipboard = navigator.clipboard as Clipboard | undefined;
  if (clipboard === undefined) {
    return "failed";
  }

  if (typeof ClipboardItem === "function") {
    try {
      await clipboard.write([
        new ClipboardItem({
          "text/plain": new Blob([flavours.text], { type: "text/plain" }),
          "text/html": new Blob([flavours.html], { type: "text/html" }),
        }),
      ]);
      return "copied";
    } catch {
      // Falls through to plain text. Safari refuses a `write` whose blobs were
      // produced asynchronously, and a page without clipboard-write permission
      // refuses it outright; in both cases `writeText` may still be allowed.
    }
  }

  try {
    await clipboard.writeText(flavours.text);
    return "copied-without-colours";
  } catch {
    return "failed";
  }
}
